import axios from 'axios';
import { 
  GenerationOptions, 
  GeneratedEmail, 
  AIEmailRequest, 
  AIModelsResponse,
  RecordatorioDetectado
} from '../types/ai';
import { API_BASE_URL } from '../config';

// Tipo para el progreso de generación
export interface GenerationProgress {
  status: 'starting' | 'in_progress' | 'completed' | 'error' | 'cancelled' | 'waiting' | 'expired';
  message: string;
  percentage?: number;
  event_type?: string;
  elapsed_time?: string;
  phase?: string;
  error?: string;
  result?: string;
  plan_valido?: boolean;
  email?: string;
  peso_detectado?: any;
  recordatorios_detectados?: RecordatorioDetectado[];
  generation_id?: string;
  updated_at?: string;
  server_time?: string;
}

export const TERMINAL_GENERATION_STATUSES = new Set<GenerationProgress['status']>([
  'completed',
  'error',
  'cancelled',
  'expired'
]);

class AIEmailService {
  async getGenerationProgress(generationId: string, signal?: AbortSignal): Promise<GenerationProgress> {
    const response = await axios.get(`${API_BASE_URL}/generation-progress/${generationId}`, {
      params: { _t: Date.now(), _r: Math.random().toString(36).slice(2) },
      timeout: 12000,
      signal,
      headers: {
        'Cache-Control': 'no-cache, no-store, max-age=0',
        'Pragma': 'no-cache',
        'Expires': '0',
        'X-Generation-Poll': generationId
      }
    });
    return response.data;
  }

  /**
   * Genera un email usando la IA del backend
   */
  async generateEmail(
    options: GenerationOptions, 
    selectedMessageId: string | null,
    emailFilters: string[],
    pacienteData?: any,
    generationId?: string,
    selectedMessageDate?: string
  ): Promise<{ result: GeneratedEmail, generationId: string }> {
    try {
      const request: AIEmailRequest = {
        selected_message_id: selectedMessageId || undefined,
        selected_message_date: selectedMessageDate || undefined,
        email_filters: emailFilters,
        incluir_plan: options.incluirPlan,
        dia_inicio: options.diaInicio,
        num_dias_objetivo: options.numDiasObjetivo,
        tone_style: options.toneStyle,
        model: options.model,
        comentario_manual: options.comentarioManual,
        editar_plan_activo: options.editarPlanActivo,
        paciente_data: pacienteData,
        generation_id: generationId,
        // NUEVO: modelo para la segunda llamada
        model_call2: options.modelCall2,
        model_judge: options.modelJudge
      };

      const response = await axios.post<GeneratedEmail>(`${API_BASE_URL}/generate-email`, request);

      // Usar el generation_id que devuelve el backend
      const responseData = response.data as any;
      return { result: response.data, generationId: responseData.generation_id || generationId || '' };
    } catch (error) {
      console.error('Error al generar email:', error);
      throw error;
    }
  }

  /**
   * Escucha el progreso de generación usando polling a /generation-progress/{generationId}
   */
  listenToProgress(
    generationId: string,
    onProgress: (progress: GenerationProgress) => void,
    onError?: (error: any) => void,
    onComplete?: () => void
  ): () => void {
    // eslint-disable-next-line no-lone-blocks
    {
      let cancelled = false;
      let timeoutId: any = null;
      let watchdogId: any = null;
      let manualAbortTimeoutId: any = null;
      let currentAbortController: AbortController | null = null;
      let pollInFlight = false;
      let lastProgressAt = Date.now();
      let consecutiveErrors = 0;
      const POLL_INTERVAL = 1500;
      const REQUEST_TIMEOUT = 12000;
      const WATCHDOG_INTERVAL = 5000;
      const STALE_PROGRESS_MS = 18000;

      const clearScheduledPoll = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      const clearManualAbortTimeout = () => {
        if (manualAbortTimeoutId) {
          clearTimeout(manualAbortTimeoutId);
          manualAbortTimeoutId = null;
        }
      };

      const abortInFlightPoll = () => {
        if (currentAbortController) {
          currentAbortController.abort();
          currentAbortController = null;
        }
        pollInFlight = false;
        clearManualAbortTimeout();
      };

      const poll = async (force = false) => {
        if (cancelled) return;

        if (pollInFlight) {
          if (!force) return;
          abortInFlightPoll();
        }

        clearScheduledPoll();
        pollInFlight = true;
        currentAbortController = new AbortController();
        const requestStartedAt = Date.now();
        clearManualAbortTimeout();
        manualAbortTimeoutId = setTimeout(() => {
          if (!cancelled && pollInFlight && Date.now() - requestStartedAt >= REQUEST_TIMEOUT) {
            abortInFlightPoll();
            poll(true);
          }
        }, REQUEST_TIMEOUT + 250);

        try {
          const progress = await this.getGenerationProgress(generationId, currentAbortController.signal);

          currentAbortController = null;
          pollInFlight = false;
          clearManualAbortTimeout();

          consecutiveErrors = 0;
          lastProgressAt = Date.now();
          onProgress(progress);

          if (TERMINAL_GENERATION_STATUSES.has(progress.status)) {
            if (onComplete) onComplete();
            return;
          }
        } catch (error: any) {
          pollInFlight = false;
          currentAbortController = null;
          clearManualAbortTimeout();

          if (axios.isCancel(error) || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
            return;
          }

          consecutiveErrors++;
          console.warn(`Error en polling (${consecutiveErrors} consecutivos), reintentando en ${POLL_INTERVAL}ms:`, error);
          if (onError) onError(error);
        }

        if (!cancelled) {
          timeoutId = setTimeout(() => poll(false), POLL_INTERVAL);
        }
      };

      const forceFreshPoll = () => {
        if (cancelled) return;
        clearScheduledPoll();
        poll(true);
      };

      const handleVisibilityChange = () => {
        if (!document.hidden) {
          forceFreshPoll();
        }
      };

      const handleResume = () => {
        forceFreshPoll();
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleResume);
      window.addEventListener('pageshow', handleResume);
      window.addEventListener('online', handleResume);

      watchdogId = setInterval(() => {
        if (cancelled || document.hidden) return;
        if (Date.now() - lastProgressAt > STALE_PROGRESS_MS) {
          forceFreshPoll();
        }
      }, WATCHDOG_INTERVAL);

      poll(false);

      const cleanupRobustPolling = () => {
        cancelled = true;
        clearScheduledPoll();
        if (watchdogId) clearInterval(watchdogId);
        abortInFlightPoll();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleResume);
        window.removeEventListener('pageshow', handleResume);
        window.removeEventListener('online', handleResume);
      };
      return cleanupRobustPolling;
    }

    /*
    const poll = async () => {
      if (cancelled) return;

      // Abortar request anterior colgado (si existe) antes de lanzar uno nuevo
      if (currentAbortController) currentAbortController.abort();
      currentAbortController = new AbortController();

      try {
        const response = await axios.get(`${API_BASE_URL}/generation-progress/${generationId}`, {
          params: { _t: Date.now() },
          timeout: 10000,
          signal: currentAbortController.signal,
          headers: {
            'Cache-Control': 'no-cache, no-store, max-age=0',
            'Pragma': 'no-cache'
          }
        });
        currentAbortController = null;
        const progress: GenerationProgress = response.data;
        consecutiveErrors = 0;
        onProgress(progress);
        if (progress.status === 'completed' || progress.status === 'error' || progress.status === 'cancelled') {
          if (onComplete) onComplete();
          return; // no schedulear más
        }
      } catch (error: any) {
        // Si fue abortado por el revival de visibilitychange: salir silenciosamente.
        // El handler ya llamó a poll() — no schedulear un segundo poll concurrente.
        if (axios.isCancel(error) || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
          return;
        }
        consecutiveErrors++;
        console.warn(`Error en polling (${consecutiveErrors} consecutivos), reintentando en ${POLL_INTERVAL}ms:`, error);
        if (onError) onError(error);
      }

      if (!cancelled) {
        timeoutId = setTimeout(poll, POLL_INTERVAL);
      }
    };

    // Cuando la pestaña vuelve a ser visible: abortar el request colgado y arrancar fresco.
    // NO se usa isPollingActive como guard — eso bloqueaba el revival en mobile Chrome.
    const handleVisibilityChange = () => {
      if (!document.hidden && !cancelled) {
        if (timeoutId) clearTimeout(timeoutId);
        if (currentAbortController) {
          currentAbortController.abort();
          currentAbortController = null;
        }
        poll();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    poll();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (currentAbortController) {
        currentAbortController.abort();
        currentAbortController = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    */
  }

  /**
   * Obtiene los modelos de IA disponibles
   */
  async getAvailableModels(): Promise<string[]> {
    try {
      const response = await axios.get<AIModelsResponse>(`${API_BASE_URL}/modelos-ia`);
      return response.data.modelos;
    } catch (error) {
      console.error('Error al obtener modelos de IA:', error);
      // Retornar modelos por defecto en caso de error
      return ['deepseek/deepseek-chat', 'openai/gpt-4', 'openai/gpt-3.5-turbo'];
    }
  }

  /**
   * Cancela la generación de email en curso
   */
  async cancelGeneration(generationId: string): Promise<void> {
    try {
      await axios.post(`${API_BASE_URL}/cancel-generation`, {
        generation_id: generationId
      });
    } catch (error) {
      console.error('Error al cancelar generación:', error);
      // No lanzar error para no interrumpir el flujo
    }
  }

  /**
   * Verifica si hay eventos duplicados en la base de datos
   */
  async checkEventosDuplicates(eventos: any[]): Promise<any> {
    try {
      const response = await axios.post(`${API_BASE_URL}/eventos/check-duplicates`, eventos);
      return response.data;
    } catch (error) {
      console.error('Error al verificar eventos duplicados:', error);
      throw error;
    }
  }

  /**
   * Crea múltiples eventos en la agenda (para recordatorios automáticos)
   */
  async createEventosBatch(eventos: any[]): Promise<void> {
    try {
      await axios.post(`${API_BASE_URL}/eventos/batch`, eventos);
    } catch (error) {
      console.error('Error al crear eventos batch:', error);
      throw error;
    }
  }
}

export const aiEmailService = new AIEmailService();
export default aiEmailService; 
